import { Snackbar, Slide, SlideProps, SnackbarCloseReason, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  closeNotification,
  removeNotification,
  selectNotifications,
  SweepNotification,
} from '../../reducers/notificationsReducer';
import { NotificationAlert } from './NotificationAlert';
import { useLocation } from 'react-router';
import { appRoutes } from '../../constants/appRoutes';
import { useTimeout } from '../../hooks/useTimeout';
import useSweepNavigate from '../../navigation-utils/useSweepNavigate';
import { useCallback } from 'react';

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="left" />;
}

export const Notifications = () => {
  const notifications = useSelector(selectNotifications);
  const dispatch = useDispatch();
  const location = useLocation();
  const { setSweepTimeout } = useTimeout();
  const { navigate } = useSweepNavigate();

  const isOnboarding = location.pathname.startsWith(appRoutes.freeTierOnBoarding.route);
  const centeredVariant = isOnboarding;

  // Split notifications by position
  const topRightNotifications = notifications.filter((n) => n.position !== 'bottom-right');
  const bottomRightNotifications = notifications.filter((n) => n.position === 'bottom-right');

  const handleNavigate = useCallback(
    (url: string) => {
      navigate(url);
    },
    [navigate],
  );

  const onClose = (notification: SweepNotification, reason?: SnackbarCloseReason) => {
    if (notification.keepOpen && reason && ['clickaway', 'escapeKeyDown'].includes(reason)) {
      return;
    }
    dispatch(closeNotification(notification.key));

    setSweepTimeout(() => {
      dispatch(removeNotification(notification.key));
    }, 1000);
  };

  const getAutoHideDuration = (notification: SweepNotification): number | null => {
    if (notification.autoHideDuration !== undefined) {
      return notification.autoHideDuration;
    }
    if (notification.keepOpen) {
      return null;
    }
    return 3000;
  };

  const renderNotification = (notification: SweepNotification, isCentered: boolean) => (
    <Snackbar
      key={notification.key}
      data-testid="notification-snackbar"
      sx={{
        position: isCentered ? 'fixed' : 'relative',
        top: isCentered ? '96px !important' : '',
        whiteSpace: 'pre-line',
      }}
      open={!notification.dismissed}
      anchorOrigin={{
        vertical: notification.position === 'bottom-right' ? 'bottom' : 'top',
        horizontal: isCentered ? 'center' : 'right',
      }}
      TransitionComponent={SlideTransition}
      autoHideDuration={getAutoHideDuration(notification)}
      onClose={(event, reason) => onClose(notification, reason)}
    >
      <NotificationAlert
        onClose={() => onClose(notification)}
        severity={notification.variant}
        message={notification.message}
        action={
          notification.action
            ? {
                actionText: notification.action.actionText,
                onActionClick: () =>
                  notification.action?.reduxAction && dispatch(notification.action.reduxAction),
              }
            : undefined
        }
        details={notification.details}
        isVariant={!!isCentered}
        linkUrl={notification.linkUrl}
        linkText={notification.linkText}
        linkOpenInNewTab={notification.linkOpenInNewTab}
        onNavigate={handleNavigate}
        theme={notification.theme}
      />
    </Snackbar>
  );

  return (
    <>
      {/* Top-right notifications */}
      <Stack
        gap={1}
        sx={{
          position: 'fixed',
          zIndex: 1502,
          top: '72px',
          right: '16px',
        }}
      >
        {topRightNotifications.map((notification) =>
          renderNotification(notification, centeredVariant),
        )}
      </Stack>

      {/* Bottom-right notifications */}
      <Stack
        gap={1}
        sx={{
          position: 'fixed',
          zIndex: 1502,
          bottom: '24px',
          right: '24px',
        }}
      >
        {bottomRightNotifications.map((notification) => renderNotification(notification, false))}
      </Stack>
    </>
  );
};
